/**
 * sl-9qm.5.2: ViewDataSourceFactory — Creates the correct IViewDataSource
 *
 * Selects the appropriate data source implementation based on source type and mode:
 * - database + builder/internal → YjsViewDataSource
 * - database + public → MaterializedViewDataSource
 * - prisma → PrismaViewDataSource
 *
 * SOLID:
 * - S: Only factory logic (no data access)
 * - O: New source types can be added without modifying existing code
 * - D: Returns IViewDataSource interface, not concrete types
 */
import { Service } from '@toeverything/infra';

import type { ViewRenderMode } from '../types/view-component.types';
import type { IViewDataSource } from '../types/view-datasource.types';
import {
  type MaterializedSnapshot,
  MaterializedViewDataSource,
} from './materialized-view-datasource';
import type { ViewGraphQLClient } from './prisma-view-datasource';
import { PrismaViewDataSource } from './prisma-view-datasource';
import { YjsViewDataSource } from './yjs-view-datasource';

/** Source type for a data binding */
export type DataSourceType = 'database' | 'prisma';

export interface CreateDataSourceOptions {
  /** Type of data source */
  sourceType: DataSourceType;
  /** Current render mode — determines which implementation to use for database sources */
  mode: ViewRenderMode;
  /** Pre-loaded snapshot for materialized view (public mode) */
  snapshot?: MaterializedSnapshot;
  /** GraphQL client for Prisma queries */
  graphqlClient?: ViewGraphQLClient;
  /** Polling interval for Prisma data source (ms) */
  pollIntervalMs?: number;
}

export class ViewDataSourceFactory extends Service {
  constructor(private readonly yjsDataSource: YjsViewDataSource) {
    super();
  }

  create(options: CreateDataSourceOptions): IViewDataSource {
    switch (options.sourceType) {
      case 'database':
        if (options.mode === 'public' && options.snapshot) {
          return new MaterializedViewDataSource(options.snapshot);
        }
        // builder + internal modes use Y.js
        return this.yjsDataSource;

      case 'prisma':
        if (!options.graphqlClient) {
          throw new Error(
            '[ViewDataSourceFactory] graphqlClient required for prisma source type'
          );
        }
        return new PrismaViewDataSource(
          options.graphqlClient,
          options.pollIntervalMs
        );

      default:
        throw new Error(
          `[ViewDataSourceFactory] Unknown source type: ${options.sourceType}`
        );
    }
  }
}
